/**
 * THE ONE AUTHORITY for "may this tab write this project record now?" (WR-685 hotfix,
 * wr685-hotfix-s1; approval-gate.md + stage4-proposal.md v2 §2).
 *
 * PROPERTY P-685:
 *   LIVE    — a tab may write a project's stored records only if no delete of that
 *             project has completed;
 *   CURRENT — it may write the drawing (`project_<id>_state`) only if the stored drawing
 *             is the one this tab's current document was loaded from, or last wrote.
 *
 * MEASURED DEFECTS this closes (wr683-diag-s1 stage3 §4, §6.4; re-measured on this tree,
 * wr685-hotfix-s1 stage3 §1): a second tab that never heard of a delete wrote the deleted
 * project's drawing back (WR-683); an IDLE second tab's unconditional unload save wrote
 * its older drawing over the other tab's newer one — permanent loss (the D1).
 *
 * HOW: `decideDocumentWrite` is pure and total. `IndexedDBManager.guardedSave` calls it
 * INSIDE the write's own read-write transaction, after reading — in that same
 * transaction — the project's metadata record (liveness) and the stored record at the
 * key (currency). IndexedDB serialises overlapping read-write transactions across every
 * tab of the origin, so the check and the write cannot interleave with another tab's
 * delete or save, and nothing depends on a message arriving.
 *
 * LIVENESS = the metadata record `project_<id>` exists. It is written FIRST when a
 * project is born (`saveProjectData`, `importService.restoreProject`) and deleted FIRST
 * when it dies (`deleteProjectRecords`); without it the project cannot open
 * (`loadProjectData`). The project LIST is deliberately NOT an input: part of it lives in
 * localStorage (outside the transaction) and it loses updates between tabs (R1c2-1..3).
 *
 * REVISION: the drawing record carries `_rev`. Absent (every record written before this
 * fix) or a missing record = 0. An allowed drawing write stores `_rev = stored + 1`.
 * The literal `_rev` lives in THIS file only (G-SHAPE a); `readRecordRev` is the only
 * reader (G-SHAPE d).
 *
 * ⛔ No module-scope work and no imports that do work (WR-682 / BOOT-TDZ-001).
 * ⛔ Never reads localStorage (G-SHAPE e): the legacy metadata fallback is read by the
 *    transaction owner and passed in as `metadataExists`.
 *
 * @see architecture/Persistence/wr685/wr685-hotfix-s1/stage4-proposal.md §2
 * @see src/__tests__/architecture/documentWriteGuardShape.test.ts (G-SHAPE)
 */

/**
 * The NAME of the drawing record's revision field — exported so a module that must know
 * which top-level keys the save path writes (develop's content model,
 * `documentVersions.PERSISTED_TOP_LEVEL_KEYS`) names it without restating the literal
 * (seat 59-85; G-SHAPE a). Measured on develop before this existed
 * (wr685-port-s1 evidence/probe-rev-collision-develop-ebc4fcde2.log): the field read as
 * residue and as CONTENT, so an empty drawing's second save replaced the real drawing's
 * only recovery copy.
 */
export const RECORD_REV_FIELD = "_rev";

/**
 * The SAVE STAMPS: top-level fields every save rewrites and that hold no user content.
 * `sameDrawing` ignores exactly these, and the recovery journal's identity is `sameDrawing`
 * (snapshots-p2a-s10 D5; F-C2). A new stamp added to the saver and NOT listed here makes every
 * save read "different" — the R-ID cells are generated over this list and the R-PERSISTED
 * census ties every "stamp" member of `PERSISTED_TOP_LEVEL_KEYS` to it.
 */
export const SAVE_STAMP_FIELDS: readonly string[] = Object.freeze(["lastSaved", RECORD_REV_FIELD]);

/** Why a write was refused. */
export type WriteRefusalReason =
  /** the project's metadata record is gone — a delete completed */
  | "deleted"
  /** the stored drawing is not the one this tab read or last wrote */
  | "stale"
  /** a drawing write that does not say which revision it replaces */
  | "no-base"
  /** a `create` of a metadata record that already exists */
  | "exists"
  /** a document key written without its owning project id */
  | "unowned"
  /** the key does not belong to the project the writer named */
  | "foreign";

/** What the writer declares about the write (carried through `StorageOptions.writeGuard`). */
export interface WriteGuard {
  /** the project that owns the record */
  projectId: string;
  /** drawing writes only: the revision this tab loaded or last wrote */
  base?: number;
  /** metadata writes only: `create` for a new project, else `update` (default) */
  intent?: "create" | "update";
}

export type WriteDecision =
  | { kind: "allow"; value: unknown; rev?: number }
  /**
   * The drawing write would store exactly what is stored: nothing is put, the revision
   * does not move, and the writer is current at `rev` (seat 59-34).
   */
  | { kind: "noop"; rev: number }
  | { kind: "refuse"; reason: WriteRefusalReason }
  | { kind: "failed"; error: unknown };

/**
 * True iff the incoming drawing is EXACTLY the stored one, ignoring only the save stamps
 * (`SAVE_STAMP_FIELDS`). Plain `JSON.stringify` (insertion order), not a canonical form: an
 * exact string compare can never report a false "identical"; a false "different" (key order)
 * falls back to the revision rule — a refusal, never a loss. Two STRINGS (raw bytes kept by
 * the recovery journal) compare by `===` (snapshots-p2a-s10 D5; FR-P11: the guard's noop for an
 * identical string drawing is unreachable — the saver never writes a string).
 * Measured (evidence/open-write-identity.log): the open-time write of a project is
 * byte-identical under both forms; canonical costs ~4× more (evidence/compare-cost.log:
 * 83 vs 19 ms at 10k objects).
 * ONE record identity: the write guard's noop AND the recovery journal's "adds none"
 * (`recoveryJournal.planRecoveryAdd`).
 */
export function sameDrawing(stored: unknown, incoming: unknown): boolean {
  if (typeof stored === "string" || typeof incoming === "string") {
    return stored === incoming;
  }
  if (
    !stored ||
    !incoming ||
    typeof stored !== "object" ||
    typeof incoming !== "object" ||
    Array.isArray(stored) ||
    Array.isArray(incoming)
  ) {
    return false;
  }
  const strip = (r: Record<string, unknown>): Record<string, unknown> => {
    const out = { ...r };
    for (const f of SAVE_STAMP_FIELDS) delete out[f];
    return out;
  };
  return (
    JSON.stringify(strip(stored as Record<string, unknown>)) ===
    JSON.stringify(strip(incoming as Record<string, unknown>))
  );
}

/** True iff `key` is a storage record belonging to `projectId` (the sweep's rule too). */
export function isProjectRecordKey(key: string, projectId: string): boolean {
  const base = `project_${projectId}`;
  return key === base || key.startsWith(`${base}_`);
}

/** The key of a project's metadata record — its existence IS the project's liveness. */
export function projectMetadataKey(projectId: string): string {
  return `project_${projectId}`;
}

/** The key of a project's drawing record. */
export function projectDrawingKey(projectId: string): string {
  return `project_${projectId}_state`;
}

/**
 * The stored revision of a drawing record: `_rev`, or 0 when the record is missing or
 * carries none (written before this fix). A `{ data }` envelope is unwrapped defensively,
 * matching `ElementStorage.manifestScenesAreEmpty`.
 */
export function readRecordRev(record: unknown): number {
  let r = record;
  if (
    r &&
    typeof r === "object" &&
    !("_rev" in (r as Record<string, unknown>)) &&
    "data" in (r as Record<string, unknown>) &&
    (r as Record<string, unknown>).data &&
    typeof (r as Record<string, unknown>).data === "object"
  ) {
    r = (r as Record<string, unknown>).data;
  }
  if (!r || typeof r !== "object") return 0;
  const rev = (r as Record<string, unknown>)._rev;
  return typeof rev === "number" && Number.isInteger(rev) && rev >= 0 ? rev : 0;
}

/**
 * Decide one `project_*` write. Total: every input yields a decision; a throw becomes
 * `failed` (never an aborted transaction retried as if it were a refusal).
 *
 * @param key            the storage key being written (a `project_*` document key)
 * @param data           the value to write
 * @param guard          what the writer declared (owner, base, intent)
 * @param metadataExists the project's metadata record exists (IndexedDB, else the legacy
 *                       localStorage copy) — read in the SAME transaction by the caller
 * @param stored         the value currently stored at `key` in IndexedDB (undefined if none)
 */
export function decideDocumentWrite(
  key: string,
  data: unknown,
  guard: WriteGuard | undefined,
  metadataExists: boolean,
  stored: unknown,
): WriteDecision {
  try {
    if (!guard || typeof guard.projectId !== "string" || guard.projectId === "") {
      return { kind: "refuse", reason: "unowned" };
    }
    const { projectId } = guard;
    if (!isProjectRecordKey(key, projectId)) {
      return { kind: "refuse", reason: "foreign" };
    }

    // The metadata record itself: born by `create`, otherwise it must already exist.
    if (key === projectMetadataKey(projectId)) {
      if (guard.intent === "create") {
        return metadataExists
          ? { kind: "refuse", reason: "exists" }
          : { kind: "allow", value: data };
      }
      return metadataExists
        ? { kind: "allow", value: data }
        : { kind: "refuse", reason: "deleted" };
    }

    // Every other record of the project: the project must be alive.
    if (!metadataExists) return { kind: "refuse", reason: "deleted" };

    // The drawing: the tab's copy must be the stored one.
    if (key === projectDrawingKey(projectId)) {
      if (typeof guard.base !== "number" || !Number.isInteger(guard.base)) {
        return { kind: "refuse", reason: "no-base" };
      }
      const storedRev = readRecordRev(stored);
      // An IDENTICAL write is idempotent (seat 59-34): opening a project re-saves it
      // unchanged a moment later (measured), and that must neither move the revision
      // under another tab nor be refused itself. Rebasing a tab whose document EQUALS
      // storage cannot overwrite anything.
      if (sameDrawing(stored, data)) return { kind: "noop", rev: storedRev };
      if (storedRev !== guard.base) return { kind: "refuse", reason: "stale" };
      const rev = storedRev + 1;
      const value =
        data && typeof data === "object" && !Array.isArray(data)
          ? { ...(data as Record<string, unknown>), _rev: rev }
          : data;
      return { kind: "allow", value, rev };
    }

    return { kind: "allow", value: data };
  } catch (error) {
    return { kind: "failed", error };
  }
}
